import type { AdminPlanDurationBucket } from '@steado/admin-contract/plan-runs';
import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@/lib/auth/auth-context';
import { formatDuration, humanise } from '@/lib/format';
import { Panel, TabSkeleton } from '@/components/panel/panels';
import { TabError } from '@/screens/user-detail/TabError';

import { planDurationsQueryOptions, type PlanRunKindFilter } from './plan-ops-query';

/** A month: long enough for p99 to mean something, short enough to be current. */
export const WINDOW_DAYS = 30;

/**
 * How long generation actually takes.
 *
 * This panel exists to settle one argument with a number. The app polls a
 * generating plan and gives up at two minutes — a threshold picked by feel —
 * and falls through to "Still building your plan · Go to home". If p95 is
 * north of that, a real share of users are being shown a give-up screen on the
 * first plan they ever generate, and the timeout is the thing to change.
 *
 * The "App gave up" column is that share, measured against the client's own
 * budget rather than a round number, so the column and the app cannot disagree
 * about what "too slow" means. The budget itself is named in the panel's
 * description, which is the column's legend.
 */
export function DurationsPanel({ kind }: { readonly kind: PlanRunKindFilter }) {
  const { api } = useAuth();
  const query = useQuery(planDurationsQueryOptions(api, WINDOW_DAYS, kind));
  const data = query.data;
  const unfinished = data?.overall.unfinished ?? 0;

  return (
    <Panel
      title="Generation duration"
      description={`Last ${String(WINDOW_DAYS)} days · the app gives up at ${formatDuration(data?.clientBudgetMs ?? 120_000) ?? ''}`}
      footer={
        // The one figure the table cannot carry: a run with no duration is in
        // no percentile, so the column totals and the percentiles disagree
        // unless the gap is stated.
        unfinished === 0 ? undefined : (
          <p className="text-[13px] text-muted-foreground">
            {unfinished === 1
              ? '1 unfinished run is not in these percentiles.'
              : `${String(unfinished)} unfinished runs are not in these percentiles.`}
          </p>
        )
      }
    >
      {query.isPending ? <TabSkeleton rows={2} /> : null}
      {query.isError ? <TabError message={query.error.message} onRetry={() => void query.refetch()} /> : null}

      {data === undefined ? null : data.overall.runs === 0 && unfinished === 0 ? (
        <p className="text-[13px] text-muted-foreground">
          No generation runs in the last {WINDOW_DAYS} days.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] border-collapse text-[13px]">
            <thead>
              {/* The console's column-header style, as `DataTable` draws it. */}
              <tr className="border-b border-border text-left text-[11.5px] font-semibold tracking-[0.055em] text-muted-foreground uppercase">
                <th className="py-2 pr-4">Trigger</th>
                <th className="py-2 pr-4 text-right">Runs</th>
                <th className="py-2 pr-4 text-right">p50</th>
                <th className="py-2 pr-4 text-right">p95</th>
                <th className="py-2 pr-4 text-right">p99</th>
                <th className="py-2 pr-4 text-right">Repaired</th>
                <th className="py-2 pr-4 text-right">App gave up</th>
                <th className="py-2 text-right">Unfinished</th>
              </tr>
            </thead>
            <tbody>
              <DurationRow label="All triggers" bucket={data.overall} emphasis />
              {data.byTrigger.map((row) => (
                <DurationRow key={row.trigger} label={humanise(row.trigger)} bucket={row} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

interface DurationRowProps {
  readonly label: string;
  readonly bucket: AdminPlanDurationBucket;
  readonly emphasis?: boolean;
}

function DurationRow({ label, bucket, emphasis = false }: DurationRowProps) {
  return (
    <tr className="border-b border-border last:border-0">
      <td className={`py-2 pr-4 ${emphasis ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>
        {label}
      </td>
      <td className="py-2 pr-4 text-right tabular-nums text-foreground">{bucket.runs}</td>
      <td className="py-2 pr-4 text-right tabular-nums text-foreground">
        {formatDuration(bucket.p50Ms) ?? '—'}
      </td>
      <td className="py-2 pr-4 text-right tabular-nums text-foreground">
        {formatDuration(bucket.p95Ms) ?? '—'}
      </td>
      <td className="py-2 pr-4 text-right tabular-nums text-foreground">
        {formatDuration(bucket.p99Ms) ?? '—'}
      </td>
      <td className="py-2 pr-4 text-right tabular-nums text-foreground">{formatShare(bucket.repairShare)}</td>
      <td className="py-2 pr-4 text-right tabular-nums text-foreground">
        {formatShare(bucket.overBudgetShare)}
      </td>
      <td className="py-2 text-right tabular-nums text-muted-foreground">{bucket.unfinished}</td>
    </tr>
  );
}

/** An em dash for null, which means "no run finished", not "none of them did". */
function formatShare(share: number | null): string {
  if (share === null) return '—';
  const percent = share * 100;
  return `${percent < 10 && percent > 0 ? percent.toFixed(1) : String(Math.round(percent))}%`;
}
